import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useUpdatePreboardingForm } from '@/hooks/use-preboarding-forms';
import { cn } from '@/lib/utils';
import type {
  PreboardingForm,
  PreboardingFormEmailContent,
  PreboardingFormEmailsConfig,
  PreboardingFormReminderConfig,
} from '@hygge/shared';
import { Info } from 'lucide-react';
import { useRef } from 'react';
import { AddVariableMenu } from '../add-variable-menu';
import { RichVariableEditor } from '../rich-variable-editor';
import { SaveStatusIndicator } from '../save-status-indicator';
import { useDraftAutosave } from '../use-draft-autosave';

export interface PreboardingFormStepProps {
  form: PreboardingForm;
}

function defaultEmailsConfig(): PreboardingFormEmailsConfig {
  return {
    invitation: { subject: '', body: '' },
    reminder: { enabled: false, days: 3, subject: '', body: '' },
    autoSendOnConversion: false,
  };
}

interface Draft {
  name: string;
  description: string;
  emails: PreboardingFormEmailsConfig;
}

function toDraft(form: PreboardingForm): Draft {
  const emails = form.emailsConfig ?? defaultEmailsConfig();
  return {
    name: form.name ?? '',
    description: form.description ?? '',
    emails: {
      invitation: {
        subject: emails.invitation?.subject ?? '',
        body: emails.invitation?.body ?? '',
      },
      reminder: {
        enabled: emails.reminder?.enabled ?? false,
        days: emails.reminder?.days ?? 3,
        subject: emails.reminder?.subject ?? '',
        body: emails.reminder?.body ?? '',
      },
      autoSendOnConversion: emails.autoSendOnConversion ?? false,
    },
  };
}

// Step 1 — Setup & Emails (HW-555). Names the form and configures the invitation
// and reminder emails. Autosaves the draft as the user edits.
export function SetupEmailsStep({ form }: PreboardingFormStepProps) {
  const updateMutation = useUpdatePreboardingForm({ suppressGlobalToast: true });

  const { draft, applyChange, status, lastSavedAt, saveNow } = useDraftAutosave<Draft>({
    initial: toDraft(form),
    onSave: async (next) => {
      const trimmedName = next.name.trim();
      await updateMutation.mutateAsync({
        id: form.id,
        input: {
          // Name is required server-side (@MinLength(1)) — skip when empty so
          // the rest of the draft still persists.
          ...(trimmedName ? { name: trimmedName } : {}),
          description: next.description.trim() || null,
          emailsConfig: next.emails,
        },
      });
    },
  });

  const nameMissing = draft.name.trim() === '';
  const invitationSubjectRef = useRef<HTMLInputElement>(null);
  const reminderSubjectRef = useRef<HTMLInputElement>(null);

  const patchEmails = (partial: Partial<PreboardingFormEmailsConfig>) =>
    applyChange({ ...draft, emails: { ...draft.emails, ...partial } });
  const patchInvitation = (p: Partial<PreboardingFormEmailContent>) =>
    patchEmails({ invitation: { ...draft.emails.invitation, ...p } });
  const patchReminder = (p: Partial<PreboardingFormReminderConfig>) =>
    patchEmails({ reminder: { ...draft.emails.reminder, ...p } });

  const insertIntoSubject = (
    ref: React.RefObject<HTMLInputElement | null>,
    current: string,
    token: string,
    setValue: (value: string) => void,
  ) => {
    const el = ref.current;
    const text = `{{${token}}}`;
    if (!el) {
      setValue(current + text);
      return;
    }
    const start = el.selectionStart ?? current.length;
    const end = el.selectionEnd ?? current.length;
    setValue(current.slice(0, start) + text + current.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + text.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const reminder = draft.emails.reminder;

  return (
    <div className="space-y-6">
      {/* Basic information */}
      <Section title="Basic information">
        <Field label="Form name" htmlFor="form-name" required error={nameMissing ? 'Form name is required' : undefined}>
          <Input
            id="form-name"
            value={draft.name}
            placeholder="e.g. Standard Pre-boarding"
            aria-invalid={nameMissing}
            onChange={(e) => applyChange({ ...draft, name: e.target.value })}
          />
        </Field>
        <Field label="Description" htmlFor="form-description" hint="Internal note about this form's purpose. Optional.">
          <Textarea
            id="form-description"
            value={draft.description}
            placeholder="Who is this form for, when to send it…"
            rows={3}
            onChange={(e) => applyChange({ ...draft, description: e.target.value })}
          />
        </Field>
      </Section>

      {/* Invitation email */}
      <Section title="Invitation email">
        <Field label="Subject" htmlFor="invitation-subject" required>
          <div className="flex items-center gap-2">
            <Input
              id="invitation-subject"
              ref={invitationSubjectRef}
              value={draft.emails.invitation.subject}
              placeholder="You're invited to complete your pre-boarding"
              onChange={(e) => patchInvitation({ subject: e.target.value })}
            />
            <AddVariableMenu
              onInsert={(token) =>
                insertIntoSubject(invitationSubjectRef, draft.emails.invitation.subject, token, (v) =>
                  patchInvitation({ subject: v }),
                )
              }
            />
          </div>
        </Field>
        <Field label="Body" required>
          <RichVariableEditor
            content={draft.emails.invitation.body}
            onChange={(html) => patchInvitation({ body: html })}
            placeholder="Hi {{employeeFirstName}}, welcome to {{companyName}}…"
          />
        </Field>
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Info className="h-3.5 w-3.5" />
          A secure “Start form” button is appended to the bottom of this email automatically.
        </p>
      </Section>

      {/* Automation */}
      <Section title="Automation">
        <label className="flex items-center gap-3 cursor-pointer">
          <Switch
            checked={draft.emails.autoSendOnConversion}
            onCheckedChange={(checked) => patchEmails({ autoSendOnConversion: checked })}
          />
          <span className="text-sm font-medium">Send automatically when a candidate is hired</span>
        </label>
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Info className="h-3.5 w-3.5" />
          When on, this published form is sent to a new hire the moment they are converted to an
          employee. Otherwise you send it manually.
        </p>
      </Section>

      {/* Reminder email */}
      <Section title="Reminder email">
        <label className="flex items-center gap-3 cursor-pointer">
          <Switch
            checked={reminder.enabled}
            onCheckedChange={(checked) => patchReminder({ enabled: checked })}
          />
          <span className="text-sm font-medium">Send an automatic reminder</span>
        </label>

        {reminder.enabled && (
          <div className="space-y-4 border-l-2 border-border pl-4">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Send a reminder if not completed within</span>
              <Input
                type="number"
                min={1}
                max={60}
                value={reminder.days}
                onChange={(e) => {
                  const n = Number.parseInt(e.target.value, 10);
                  patchReminder({ days: Number.isNaN(n) ? 1 : Math.min(60, Math.max(1, n)) });
                }}
                className="h-8 w-20"
              />
              <span className="text-muted-foreground">days</span>
            </div>

            <Field label="Subject" htmlFor="reminder-subject">
              <div className="flex items-center gap-2">
                <Input
                  id="reminder-subject"
                  ref={reminderSubjectRef}
                  value={reminder.subject}
                  placeholder="Reminder: please complete your pre-boarding form"
                  onChange={(e) => patchReminder({ subject: e.target.value })}
                />
                <AddVariableMenu
                  onInsert={(token) =>
                    insertIntoSubject(reminderSubjectRef, reminder.subject, token, (v) =>
                      patchReminder({ subject: v }),
                    )
                  }
                />
              </div>
            </Field>
            <Field label="Body">
              <RichVariableEditor
                content={reminder.body}
                onChange={(html) => patchReminder({ body: html })}
                placeholder="Just a quick reminder, {{employeeFirstName}}…"
              />
            </Field>
          </div>
        )}
      </Section>

      <div className="flex justify-end">
        <SaveStatusIndicator status={status} lastSavedAt={lastSavedAt} onRetry={saveNow} />
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4 rounded-xl border border-border bg-card p-5">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {children}
    </section>
  );
}

function Field({
  label,
  htmlFor,
  required,
  hint,
  error,
  children,
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className={cn(error && 'text-destructive')}>
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </Label>
      {children}
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}
